import React from "react";
import readyQuizWallpaper from "../../../../assets/images/course/start-quiz.svg";
import { Button, Alert } from "react-bootstrap";
import { Shield, RefreshCw, RotateCcw, Award } from "react-feather";

const StatTile = ({ icon, label, value, tone = "dark" }) => (
  <div
    className="d-flex align-items-center gap-2 px-3 py-2 rounded-3 border bg-white shadow-sm"
    style={{ minWidth: 150 }}
  >
    <span className={`text-${tone}`}>{icon}</span>
    <div className="text-start">
      <div
        className="text-muted text-uppercase"
        style={{ fontSize: "0.68rem", letterSpacing: "0.04em", lineHeight: 1 }}
      >
        {label}
      </div>
      <div className="fw-bold text-dark" style={{ fontSize: "1.15rem", lineHeight: 1.4 }}>
        {value}
      </div>
    </div>
  </div>
);

const ReadyForQuiz = ({
  startQuizHandler,
  questions,
  passingPercentage,
  title = "Ready For Quiz",
  description = "Test your knowledge, earn a score, and discover where you stand with this engaging quiz!",
  buttonText = "Let’s Start",
  isDisabled = false,
  showStats = true,
  isProctored = false,
  maxAttempts = null,
  remainingAttempts = null,
}) => {
  const hasAttemptsLimit = Boolean(maxAttempts);
  const isOutOfAttempts = hasAttemptsLimit && remainingAttempts === 0;
  return (
    <>
      <div className="d-flex flex-column align-items-center justify-content-center ">
        <div>
          <img src={readyQuizWallpaper} alt="quiz status" />
        </div>
        <div className="d-flex flex-column align-items-center justify-content-center mt-3">
          <h2>{title}</h2>
          <p className="text-center">
            {description}
          </p>

          {showStats && (
            <div className="d-flex flex-row justify-content-between w-100 px-4">
              {questions?.length > 0 && (
                <p>
                  Number of Questions:{" "}
                  <span style={{ fontWeight: "bold" }}>{questions?.length}</span>
                </p>
              )}
              <p>
                Passing Percentage:{" "}
                <span style={{ fontWeight: "bold" }}>{passingPercentage}%</span>
              </p>
            </div>
          )}

          {hasAttemptsLimit && (
            <div className="d-flex flex-wrap justify-content-center gap-3 w-100 px-4 mt-1 mb-1">
              <StatTile icon={<RotateCcw size={18} />} label="Max Attempts" value={maxAttempts} />
              <StatTile
                icon={<Award size={18} />}
                label="Attempts Left"
                value={remainingAttempts}
                tone={isOutOfAttempts ? "danger" : remainingAttempts <= 1 ? "warning" : "success"}
              />
            </div>
          )}

          {hasAttemptsLimit && !isOutOfAttempts && (
            <Alert variant="info" className="mt-2" style={{ maxWidth: "560px" }}>
              <div className="d-flex align-items-start">
                <RefreshCw size={20} className="me-2 flex-shrink-0 mt-1" />
                <div>
                  <strong>Important — Do not refresh, navigate away, or leave this page once you start.</strong>
                  <p className="mb-0 mt-1 small">
                    If you refresh, close the tab, lose your network connection, or navigate to
                    another page while the quiz is in progress, it will be automatically
                    submitted with your current answers and will count as one used attempt.
                  </p>
                </div>
              </div>
            </Alert>
          )}

          {isProctored && (
            <Alert variant="warning" className="mt-2" style={{ maxWidth: "560px" }}>
              <div className="d-flex align-items-start">
                <Shield size={20} className="me-2 flex-shrink-0 mt-1" />
                <div>
                  <strong>This is a Proctored Quiz</strong>
                  <ul className="mb-0 mt-1 ps-3 small">
                    <li>The quiz will open in fullscreen mode - do not exit it.</li>
                    <li>Do not switch tabs or windows while attempting the quiz.</li>
                    <li>Copy, paste, and right-click are disabled.</li>
                    <li>Developer tools and related shortcuts are blocked.</li>
                  </ul>
                </div>
              </div>
            </Alert>
          )}

          <Button
            onClick={startQuizHandler}
            disabled={isDisabled || isOutOfAttempts || (showStats && questions?.length === 0)}
            className="px-5 py-2 fw-bold shadow-sm mt-2"
          >
            {isOutOfAttempts ? "No Attempts Remaining" : buttonText}
          </Button>

          {showStats && questions?.length === 0 && (
            <p style={{ color: "red", paddingTop: "10px", fontSize: "12px" }}>
              No Questions available in quiz.
            </p>
          )}

          {isOutOfAttempts && (
            <p style={{ color: "red", paddingTop: "10px", fontSize: "12px" }}>
              You have used all {maxAttempts} of your attempts for this quiz.
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default ReadyForQuiz;
